import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import React, { useMemo, useEffect } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { withRouter } from 'react-router-dom';
import { MessageType } from '@upstart/harmony/lib/legos/websiteTypes';
import { isEmployeeOrAdvancedUser } from '@upstart/harmony/lib/legos/userTypes';
import { Dealer } from '@upstart/harmony/lib/legos/dealerTypes';
import {
  signalSessionTimedOutToCC,
  isEmbeddedInCommandCenter,
} from 'util/commandCenter';

import { AuthForm, AccessDealerForm } from '../../components/AuthForm';
import { PageMessage } from '../../components/GlobalUI';
import authActions from '../../redux/auth/actions';
import userActions from '../../redux/user/actions';
import loginActions from '../../redux/logins/actions';
import { parseError } from './error-handling';
import logo from '../../assets/logo.png';
import './Auth.scss';
import OTPForm from '../../components/OTPForm/OTPForm';

function Auth(props: any) {
  const { actions, auth, user, logins, location, didResetPwd } = props;
  const { currentUser, currentUserDealers } = user;
  const { mfaToken } = logins;

  // Ensure the request only happens once
  const oktaRequestRef = React.useRef<boolean>(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const code = searchParams.get('code');
    const state = searchParams.get('state');

    if (code && state && !oktaRequestRef.current) {
      oktaRequestRef.current = true;
      actions.authRequest({ code, state, skipMfa: false });
    }
  }, []);

  useEffect(() => {
    actions.redirectIfLoggedIn();
    if (location?.pathname === '/expiredSession') {
      PageMessage({
        message: 'Your session expired. Please log in to continue.',
        type: MessageType.INFO,
      });
      // chairman can think it has timed out even though the session is still valid when embedded in CC and in background tab
      // https://upstartnetwork.atlassian.net/browse/REL-16070
      if (isEmbeddedInCommandCenter()) {
        signalSessionTimedOutToCC();
      }
    }
  }, []);

  const onContinueWithoutDealer = () => {
    actions.accessAdminRequest();
  };

  const onSelectDealer = (dealer: Dealer) => {
    actions.accessDealerRequest(dealer);
  };

  const handleSkipMFA = () => {
    const { email, password } = logins.loginCred;
    actions.authRequest({ email, password, skipMfa: true });
  };

  const sortDealerByName = (a: Dealer, b: Dealer) => {
    const aName = (a.name || '').toLowerCase();
    const bName = (b.name || '').toLowerCase();

    if (aName > bName) return 1;
    if (aName < bName) return -1;
    return 0;
  };

  const sortedCurrentUserDealers = useMemo(
    () => currentUserDealers.sort(sortDealerByName),
    [currentUserDealers],
  );

  const getContent = () => {
    if (!currentUser) {
      if (mfaToken) {
        return (
          <OTPForm
            mfaToken={mfaToken}
            handleSubmit={actions.otpVerifyRequest}
            handleResendOTP={actions.otpResendRequest}
            skipMfaExplicitlyEnabled={logins.legacyLoginDisabled === false}
            handleSkipMFA={handleSkipMFA}
            handleBackToLogIn={actions.deleteMfaLoginToken}
            fetching={auth.fetching}
            error={parseError(auth.error)}
          />
        );
      }

      return (
        <AuthForm
          onSubmit={actions.authRequest}
          onOktaSubmit={actions.oktaRedirect}
          fetching={auth.fetching}
          error={parseError(auth.error)}
          didResetPwd={didResetPwd}
        />
      );
    }

    if (currentUser && isEmployeeOrAdvancedUser(currentUser)) {
      return (
        <AccessDealerForm
          user={currentUser}
          dealers={sortedCurrentUserDealers}
          onSelectDealer={onSelectDealer}
          onContinueWithoutDealer={onContinueWithoutDealer}
          onLogout={actions.logoutRequest}
        />
      );
    }

    return null;
  };

  return (
    <>
      <div className="auth-container">
        <img src={logo} alt="logo" className="auth-logo" />
        {getContent()}
      </div>
      {!currentUser && !mfaToken && (
        <div className="auth-link-container">
          <a
            href={`${process.env.REACT_APP_COMMAND_CENTER_URL}/forgot-password`}
            target="_blank"
            rel="noreferrer"
          >
            Forgot password?
          </a>
        </div>
      )}
    </>
  );
}

Auth.propTypes = {
  auth: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  logins: PropTypes.object.isRequired,
  location: PropTypes.object,
};

const mapStateToProps = (state: any) => ({
  auth: state.auth,
  user: state.user,
  logins: state.logins,
});

const mapDispatchToProps = (dispatch: Dispatch<{}>) => ({
  actions: bindActionCreators(
    { ...authActions, ...userActions, ...loginActions },
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Auth));
